import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  AlertCircle,
  CalendarCheck2,
  Check,
  CheckCircle2,
  Clock3,
  Info,
  Loader2,
  Mail,
  ShieldAlert,
  Ticket,
  UserRound,
  X,
  XCircle,
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { withErrorCode } from '@/lib/errors'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { humanizeParticipantPackageError, participantPackageService } from '../services/participantPackage.service'
import type {
  ParticipantConfirmationContext,
  ParticipantOfferStatus,
} from '../types'
import { PerformanceAllocation, getAllocationTotal } from './PerformanceAllocation'
import { ParticipantPackagePayment } from './ParticipantPackagePayment'
import { ParticipantPackageSummary } from './ParticipantPackageSummary'

interface ParticipantPackageFlowProps {
  context: ParticipantConfirmationContext
  productionSlug: string
  submissionId: string
  token: string
  onContextChange: (context: ParticipantConfirmationContext) => void
}

type ConfirmAction = 'decline' | 'withdraw' | null
type ResolvedOfferStatus = ParticipantOfferStatus | 'legacy_accepted'

const DEFAULT_NON_REFUNDABLE_POLICY =
  'I understand that this participant package is non-refundable, including if I withdraw or cannot participate. Withdrawing forfeits my participant spot, but my purchased admission tickets remain valid.'

export function ParticipantPackageFlow({
  context,
  productionSlug,
  submissionId,
  token,
  onContextChange,
}: ParticipantPackageFlowProps) {
  const packageSnapshot = context.participantPackage
  const performances = context.performanceOptions ?? []
  const offerStatus = resolveOfferStatus(context)
  const [selection, setSelection] = useState<Record<string, number>>(() => getInitialSelection(context))
  const [acknowledged, setAcknowledged] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingSelection, setIsSavingSelection] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    setSelection(getInitialSelection(context))
    setAcknowledged(false)
    setActionError(null)
  }, [context])

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(intervalId)
  }, [])

  const effectiveSelection = useMemo(
    () => getEffectiveSelection(context, selection),
    [context, selection]
  )
  const savedSelection = useMemo(() => getInitialSelection(context), [context])
  const allocationTotal = getAllocationTotal(effectiveSelection)
  const selectionDirty = !sameQuantities(effectiveSelection, savedSelection)
  const deadlineClosed = hasClosedDeadline(context, now)
  const linkExpired =
    context.isLinkExpired === true ||
    (context.linkExpiresAt !== undefined && isPast(context.linkExpiresAt, now))
  const selectionIsValid = Boolean(
    packageSnapshot &&
      performances.length > 0 &&
      allocationTotal === packageSnapshot.includedTicketQuantity
  )

  async function refreshContext() {
    const nextContext = await participantPackageService.getConfirmationContext({
      productionSlug,
      submissionId,
      token,
    })
    onContextChange(nextContext)
    return nextContext
  }

  async function handleAccept() {
    if (isSubmitting || !packageSnapshot) return
    setActionError(null)
    setIsSubmitting(true)

    try {
      await participantPackageService.respond({
        productionSlug,
        submissionId,
        token,
        response: 'accept',
        performanceQuantities: effectiveSelection,
        acknowledged,
        idempotencyKey: crypto.randomUUID(),
      })
      await refreshContext()
    } catch (error) {
      setActionError(humanizeParticipantPackageError(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleLegacyAccept() {
    if (isSubmitting) return
    setActionError(null)
    setIsSubmitting(true)

    try {
      await participantPackageService.respond({
        productionSlug,
        submissionId,
        token,
        response: 'accept',
        idempotencyKey: crypto.randomUUID(),
      })
      await refreshContext()
    } catch (error) {
      setActionError(humanizeParticipantPackageError(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleDecline() {
    if (isSubmitting) return
    setActionError(null)
    setIsSubmitting(true)

    try {
      await participantPackageService.respond({
        productionSlug,
        submissionId,
        token,
        response: 'decline',
        idempotencyKey: crypto.randomUUID(),
      })
      setConfirmAction(null)
      await refreshContext()
    } catch (error) {
      setActionError(humanizeParticipantPackageError(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleSelectionSave() {
    if (!packageSnapshot || !selectionIsValid || isSavingSelection || deadlineClosed) return
    setActionError(null)
    setIsSavingSelection(true)

    try {
      await participantPackageService.updatePerformanceSelection({
        productionSlug,
        submissionId,
        token,
        performanceQuantities: effectiveSelection,
        idempotencyKey: crypto.randomUUID(),
      })
      await refreshContext()
    } catch (error) {
      setActionError(humanizeParticipantPackageError(error))
    } finally {
      setIsSavingSelection(false)
    }
  }

  async function handleWithdrawal() {
    if (isSubmitting) return
    setActionError(null)
    setIsSubmitting(true)

    try {
      await participantPackageService.withdraw({
        productionSlug,
        submissionId,
        token,
        idempotencyKey: crypto.randomUUID(),
      })
      setConfirmAction(null)
      await refreshContext()
    } catch (error) {
      setActionError(humanizeParticipantPackageError(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  function withActionDialog(content: ReactNode) {
    return (
      <>
        {content}
        {actionDialog()}
      </>
    )
  }

  function renderLegacyFlow() {
    if (offerStatus === 'legacy_accepted') {
      return withActionDialog(
        <StatusCard
          icon={<CheckCircle2 className="h-6 w-6" aria-hidden="true" />}
          title="Your spot is confirmed"
          description={context.requirements?.length ? `Thanks for accepting your spot in ${context.productionName}. Complete your required materials below. Our team will review and verify them.` : `Thanks for confirming your spot in ${context.productionName}. The production team will be in touch with next steps soon.`}
          tone="success"
        />
      )
    }

    return withActionDialog(
      <Card>
        <CardContent className="space-y-6 p-6 sm:p-8">
          <InvitationHeading context={context} />
          <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm">
            Hi <strong>{context.applicantFirstName}</strong> — please confirm whether you are able
            to participate in <strong>{context.productionName}</strong>.
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button size="lg" onClick={() => void handleLegacyAccept()} disabled={isSubmitting} className="sm:min-w-40">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
              Accept my spot
            </Button>
            <Button size="lg" variant="outline" onClick={() => setConfirmAction('decline')} disabled={isSubmitting} className="sm:min-w-40">
              <X className="h-4 w-4" aria-hidden="true" />
              Decline
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (linkExpired) {
    return withActionDialog(
      <StatusCard
        icon={<Clock3 className="h-6 w-6" aria-hidden="true" />}
        title="Invitation link expired"
        description="This secure invitation link is no longer active. Please use the latest invitation email or contact the production team for help."
        tone="neutral"
        errorCode="participant-package/link-expired"
      />
    )
  }

  if (!packageSnapshot) return renderLegacyFlow()

  if (offerStatus === 'waitlisted') {
    return withActionDialog(
      <StatusCard
        icon={<Clock3 className="h-6 w-6" aria-hidden="true" />}
        title="You’re on the waitlist"
        description={`Your application is still in consideration for ${context.productionName}. If a participant spot opens, we’ll email you a secure invitation.`}
        tone="neutral"
      />
    )
  }

  if (offerStatus === 'invited') {
    return withActionDialog(
      <div className="space-y-5">
        <Card>
          <CardContent className="space-y-6 p-6 sm:p-8">
            <InvitationHeading context={context} />
            <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm">
              Hi <strong>{context.applicantFirstName}</strong> — congratulations. Review your
              participant package{performances.length > 1 ? ' and choose your performance dates' : ''} before accepting.
            </div>

            <ParticipantPackageSummary
              packageSnapshot={packageSnapshot}
              deadlines={context.deadlines}
              performances={performances}
              performanceQuantities={effectiveSelection}
            />
            <AccountPrompt />

            {performances.length !== 1 && (
              <PerformanceAllocation
                performances={performances}
                includedTicketQuantity={packageSnapshot.includedTicketQuantity}
                quantities={selection}
                onChange={setSelection}
                disabled={isSubmitting}
                describedBy="participant-acceptance-selection-help"
              />
            )}
            {performances.length > 1 && (
              <p id="participant-acceptance-selection-help" className="text-xs text-muted-foreground">
                Your selections are reserved atomically when you accept. The server will perform one
                final capacity check before committing them.
              </p>
            )}

            <div className="flex items-start gap-3 rounded-lg border p-4">
              <Checkbox
                id="participant-non-refundable-acceptance"
                checked={acknowledged}
                onCheckedChange={(checked) => setAcknowledged(checked === true)}
                disabled={isSubmitting}
                required
                aria-required="true"
                aria-invalid={!acknowledged}
              />
              <label htmlFor="participant-non-refundable-acceptance" className="cursor-pointer text-sm leading-6">
                {packageSnapshot.nonRefundablePolicyText || DEFAULT_NON_REFUNDABLE_POLICY}
              </label>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-muted-foreground">
                You can change your mind before accepting. Once paid, the package cannot be refunded.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button size="lg" variant="outline" onClick={() => setConfirmAction('decline')} disabled={isSubmitting}>
                  <X className="h-4 w-4" aria-hidden="true" />
                  Decline
                </Button>
                <Button
                  size="lg"
                  onClick={() => void handleAccept()}
                  disabled={isSubmitting || !acknowledged || !selectionIsValid}
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
                  Accept and reserve my spot
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        {actionError && <ActionError message={actionError} />}
      </div>
    )
  }

  if (offerStatus === 'accepted_unpaid') {
    return withActionDialog(
      <div className="space-y-5">
        <Card>
          <CardContent className="space-y-5 p-6 sm:p-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <Badge variant="secondary" className="mb-3">
                  <Clock3 aria-hidden="true" /> Payment pending
                </Badge>
                <h1 className="text-2xl font-bold tracking-tight">Your spot is reserved</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  Your selected performance capacity is held while you complete payment.
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CalendarCheck2 className="h-6 w-6" aria-hidden="true" />
              </div>
            </div>

            <ParticipantPackageSummary
              packageSnapshot={packageSnapshot}
              deadlines={context.deadlines}
              performances={performances}
              performanceQuantities={effectiveSelection}
            />
            <AccountPrompt />

            {performances.length > 1 && (
              <div className="space-y-3 rounded-xl border p-4 sm:p-5">
                <div>
                  <h2 className="font-semibold">Need to adjust your dates?</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    You may change your performance allocation until payment. Your existing hold is
                    swapped atomically only when all new seats are available.
                  </p>
                </div>
                <PerformanceAllocation
                  performances={performances}
                  includedTicketQuantity={packageSnapshot.includedTicketQuantity}
                  quantities={selection}
                  onChange={setSelection}
                  disabled={isSavingSelection || isSubmitting || deadlineClosed}
                />
                {selectionDirty && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void handleSelectionSave()}
                    disabled={isSavingSelection || !selectionIsValid || deadlineClosed}
                  >
                    {isSavingSelection && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                    {isSavingSelection ? 'Saving dates…' : 'Save performance changes'}
                  </Button>
                )}
              </div>
            )}

            {deadlineClosed && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>Payment is no longer available</AlertTitle>
                <AlertDescription>
                  {withErrorCode(
                    'This invitation has passed its payment deadline or hard show cutoff. The production team will process the forfeiture and contact the next applicant.',
                    'participant-package/payment-deadline-passed',
                  )}
                </AlertDescription>
              </Alert>
            )}

            {selectionDirty && !deadlineClosed && (
              <Alert>
                <Info className="h-4 w-4" aria-hidden="true" />
                <AlertDescription>Save your performance changes before starting payment.</AlertDescription>
              </Alert>
            )}

            <ParticipantPackagePayment
              priceCents={packageSnapshot.priceCents}
              currency={packageSnapshot.currency}
              policyText={packageSnapshot.nonRefundablePolicyText || DEFAULT_NON_REFUNDABLE_POLICY}
              paymentDueAt={context.deadlines?.paymentDueAt}
              paymentExpiryAt={context.deadlines?.paymentExpiryAt}
              hardCutoffAt={context.deadlines?.hardCutoffAt}
              paymentStatus={context.payment?.status}
              failureMessage={context.payment?.failureMessage}
              disabled={deadlineClosed || selectionDirty || !selectionIsValid || isSavingSelection}
              onCreateCheckout={(idempotencyKey, checkoutAcknowledged) =>
                participantPackageService.createCheckout({
                  productionSlug,
                  submissionId,
                  token,
                  idempotencyKey,
                  acknowledged: checkoutAcknowledged,
                  returnUrl: window.location.href,
                })
              }
              onPaymentSubmitted={refreshContext}
            />

            {actionError && <ActionError message={actionError} />}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (offerStatus === 'paid') {
    return withActionDialog(
      <div className="space-y-5">
        <Card>
          <CardContent className="space-y-5 p-6 text-center sm:p-8">
            <StatusCard
              icon={<CheckCircle2 className="h-6 w-6" aria-hidden="true" />}
              title="Payment received"
              description={`Your ${context.productionName} participant package is paid and your tickets are being delivered to your email.`}
              tone="success"
              bare
            />
            <ParticipantPackageSummary
              packageSnapshot={packageSnapshot}
              deadlines={context.deadlines}
              performances={performances}
              performanceQuantities={effectiveSelection}
              heading="Paid participant package"
            />
            <AccountPrompt />
            <div className="flex items-start gap-3 rounded-lg border border-dashed bg-muted/30 p-4 text-left text-sm text-muted-foreground">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <p>
                Your individual tickets will be available through the secure ticket link in your
                confirmation email. Each ticket can be transferred until it is checked in.
              </p>
            </div>
            <Button type="button" variant="outline" onClick={() => setConfirmAction('withdraw')} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Withdraw from the production
            </Button>
            {actionError && <ActionError message={actionError} />}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (offerStatus === 'declined') {
    return withActionDialog(
      <StatusCard
        icon={<XCircle className="h-6 w-6" aria-hidden="true" />}
        title="Response recorded"
        description={`We recorded your decision not to participate in ${context.productionName}. Thank you for letting us know.`}
        tone="neutral"
      />
    )
  }

  if (offerStatus === 'payment_forfeited') {
    return withActionDialog(
      <StatusCard
        icon={<Clock3 className="h-6 w-6" aria-hidden="true" />}
        title="The invitation was forfeited"
        description="The payment deadline passed without a completed payment. Your reservation was released and the production team was notified so the next applicant could be contacted."
        tone="warning"
        errorCode="participant-package/payment-deadline-passed"
      />
    )
  }

  if (offerStatus === 'withdrawn') {
    return withActionDialog(
      <div className="space-y-5">
        <StatusCard
          icon={<ShieldAlert className="h-6 w-6" aria-hidden="true" />}
          title="You withdrew from the production"
          description="Your participant spot has been released and the next applicant may be invited. Your payment was non-refundable, and your purchased tickets remain valid."
          tone="warning"
        />
        <Card>
          <CardContent className="flex items-start gap-3 p-5 text-sm text-muted-foreground">
            <Ticket className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <p>You can still use or transfer your purchased tickets until they are checked in.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (offerStatus === 'promotion_blocked_capacity') {
    return withActionDialog(
      <StatusCard
        icon={<ShieldAlert className="h-6 w-6" aria-hidden="true" />}
        title="The next invitation is waiting on capacity"
        description="There is not enough remaining performance capacity to support this package. The production team has been notified and will follow up."
        tone="warning"
        errorCode="participant-package/promotion-capacity"
      />
    )
  }

  if (offerStatus === 'promotion_blocked_cutoff') {
    return withActionDialog(
      <StatusCard
        icon={<Clock3 className="h-6 w-6" aria-hidden="true" />}
        title="The payment cutoff has passed"
        description="This invitation could not be opened because the hard show cutoff has passed. The production team has been notified."
        tone="warning"
        errorCode="participant-package/payment-cutoff"
      />
    )
  }

  return withActionDialog(
    <StatusCard
      icon={<AlertCircle className="h-6 w-6" aria-hidden="true" />}
      title="This invitation is not available"
      description="The production team has been notified. Please use the latest invitation email or contact the team for help."
      tone="neutral"
      errorCode="participant-package/unavailable"
    />
  )

  async function handleActionConfirm() {
    if (confirmAction === 'decline') await handleDecline()
    if (confirmAction === 'withdraw') await handleWithdrawal()
  }

  function actionDialog() {
    if (!confirmAction) return null
    const isDecline = confirmAction === 'decline'
    return (
      <Dialog open onOpenChange={(open) => !open && !isSubmitting && setConfirmAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isDecline ? 'Decline this invitation?' : 'Withdraw from the production?'}</DialogTitle>
            <DialogDescription>
              {isDecline
                ? 'Declining releases any reserved performance capacity and allows the next waitlisted applicant to be invited. You will not be able to use this invitation afterward.'
                : 'Withdrawing releases your production spot so the next applicant can be considered. Your payment will not be refunded, but every purchased ticket remains valid and transferable.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmAction(null)} disabled={isSubmitting}>
              Keep my spot
            </Button>
            <Button type="button" variant={isDecline ? 'destructive' : 'default'} onClick={() => void handleActionConfirm()} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {isDecline ? 'Decline invitation' : 'Confirm withdrawal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }
}

function InvitationHeading({ context }: { context: ParticipantConfirmationContext }) {
  return (
    <div className="space-y-2 text-center">
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">You&apos;re invited</p>
      <h1 className="text-3xl font-bold tracking-tight">{context.productionName}</h1>
      <p className="text-sm text-muted-foreground">
        {context.venue} · {formatProductionDateRange(context.startDate, context.endDate)}
      </p>
      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
        Invitation for {context.applicantFirstName} {context.applicantLastName}
      </p>
    </div>
  )
}

function StatusCard({
  icon,
  title,
  description,
  tone,
  errorCode,
  bare = false,
}: {
  icon: ReactNode
  title: string
  description: string
  tone: 'success' | 'warning' | 'neutral'
  errorCode?: string
  bare?: boolean
}) {
  const displayDescription = errorCode
    ? withErrorCode(description, errorCode)
    : description

  if (bare) {
    return (
      <div className="space-y-3">
        <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${statusTone(tone).icon}`}>
          {icon}
        </div>
        <h1 className="text-2xl font-bold">{title}</h1>
        <p className="text-sm text-muted-foreground">{displayDescription}</p>
      </div>
    )
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-8 text-center sm:p-10">
        <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${statusTone(tone).icon}`}>
          {icon}
        </div>
        <h1 className="text-2xl font-bold">{title}</h1>
        <p className="mx-auto max-w-lg text-sm text-muted-foreground">{displayDescription}</p>
      </CardContent>
    </Card>
  )
}

function ActionError({ message }: { message: string }) {
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" aria-hidden="true" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  )
}

function AccountPrompt() {
  const location = useLocation()
  const returnTo = `${location.pathname}${location.search}`
  return (
    <p className="text-center text-xs text-muted-foreground">
      Prefer to use an account?{' '}
      <Link
        to="/sign-in"
        state={{ returnTo }}
        className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
      >
        Sign in
      </Link>{' '}
      if you prefer. You can also continue securely with this email link as a guest.
    </p>
  )
}

function statusTone(tone: 'success' | 'warning' | 'neutral') {
  if (tone === 'success') return { icon: 'bg-emerald-500/15 text-emerald-600' }
  if (tone === 'warning') return { icon: 'bg-amber-500/15 text-amber-600' }
  return { icon: 'bg-muted text-muted-foreground' }
}

function resolveOfferStatus(context: ParticipantConfirmationContext): ResolvedOfferStatus {
  if (context.offerStatus) return context.offerStatus
  if (context.currentResponse === 'declined') return 'declined'
  if (context.currentResponse === 'accepted') return 'legacy_accepted'
  return 'invited'
}

function getInitialSelection(context: ParticipantConfirmationContext): Record<string, number> {
  if (context.selectedPerformanceQuantities) {
    return normalizeQuantities(context.selectedPerformanceQuantities)
  }

  const firstPerformance = context.performanceOptions?.[0]
  if (context.participantPackage && context.performanceOptions?.length === 1 && firstPerformance) {
    return { [firstPerformance.performanceId]: context.participantPackage.includedTicketQuantity }
  }
  return {}
}

function getEffectiveSelection(
  context: ParticipantConfirmationContext,
  selection: Record<string, number>
): Record<string, number> {
  if (context.performanceOptions?.length === 1 && context.participantPackage) {
    const performance = context.performanceOptions[0]
    if (performance) {
      return { [performance.performanceId]: context.participantPackage.includedTicketQuantity }
    }
  }
  return normalizeQuantities(selection)
}

function normalizeQuantities(quantities: Record<string, number>): Record<string, number> {
  return Object.fromEntries(
    Object.entries(quantities)
      .map(([key, value]): [string, number] => [
        key,
        Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0,
      ])
      .filter(([, value]) => value > 0)
  )
}

function sameQuantities(left: Record<string, number>, right: Record<string, number>): boolean {
  const normalizedLeft = normalizeQuantities(left)
  const normalizedRight = normalizeQuantities(right)
  const keys = new Set([...Object.keys(normalizedLeft), ...Object.keys(normalizedRight)])
  return [...keys].every((key) => (normalizedLeft[key] ?? 0) === (normalizedRight[key] ?? 0))
}

function hasClosedDeadline(context: ParticipantConfirmationContext, now: number): boolean {
  if (!context.deadlines) return false
  const paymentDue = new Date(context.deadlines.paymentDueAt).getTime()
  const hardCutoff = new Date(context.deadlines.hardCutoffAt).getTime()
  return (Number.isFinite(paymentDue) && paymentDue <= now) || (Number.isFinite(hardCutoff) && hardCutoff <= now)
}

function isPast(iso: string, now: number): boolean {
  const timestamp = new Date(iso).getTime()
  return Number.isFinite(timestamp) && timestamp <= now
}

function formatProductionDateRange(start: string, end?: string): string {
  const startDate = formatProductionDate(start)
  return end ? `${startDate} – ${formatProductionDate(end)}` : startDate
}

function formatProductionDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
}
